<ng-container *transloco="let t">
  <IBillingInfoForm
    [formValue]="customer()"
    [customer]="customer()"
    [stripePublishableKey]="stripePublicKey"
    (selectedCountry)="customerCountry.set($event)"
    [requiredCard]="requiredCard()"
    [firstInvoiceDate]="formattedFirstProChargeDate()"
    [submitLabel]="t('upgrade-from-autopilot-modal.upgrade')"
    (billingFormValueChange)="upgrade()"
  >
    <ng-container *ngTemplateOutlet="plan" />
    <ng-container ngProjectAs="[billingInfoFooter]">
      <ng-container *ngTemplateOutlet="upgradeInformation" />
    </ng-container>
  </IBillingInfoForm>

  <ng-template #plan>
    <div class="flex flex-col gap-3">
      <div class="flex flex-col gap-4 rounded-xl border p-5">
        <div>
          <div class="text-foreground text-2xl font-semibold tracking-tight">{{ t("plan-card.m19_professional") }}</div>
          <div class="type-body text-muted-foreground">{{ t("explore-pro-modal.pro_subtitle") }}</div>
        </div>
        <div>
          @if (isAmountLoading()) {
            <div class="h-8 w-36 animate-pulse rounded-md bg-slate-200"></div>
          } @else {
            <div class="text-foreground text-3xl font-semibold tracking-tight">
              {{ formattedPlanAmount() }} + {{ selectedPlan()?.adSpendFee?.amount ?? 0 | percent: "1.0-2" : locale() }}
            </div>
          }
          <div class="type-caption text-muted-foreground">
            {{
              selectedPlan()?.adSpendFee?.type === AdSpendFeeType.MANAGED
                ? t("explore-pro-modal.managed_ad_spend_caption")
                : t("explore-pro-modal.total_ad_spend_caption")
            }}
          </div>
        </div>
        <ul class="flex flex-col gap-2">
          @for (feature of proFeatures; track feature) {
            <ng-container *ngTemplateOutlet="proFeature; context: { $implicit: t(feature) }" />
          }
          @if (showMoreFeatures()) {
            @for (feature of moreProFeatures; track feature) {
              <ng-container *ngTemplateOutlet="proFeature; context: { $implicit: t(feature) }" />
            }
          }
        </ul>
        <button
          type="button"
          class="type-body text-muted-foreground flex items-center gap-1 self-start"
          (click)="showMoreFeatures.set(!showMoreFeatures())"
        >
          {{
            showMoreFeatures()
              ? t("upgrade-from-autopilot-modal.see_less_features")
              : t("upgrade-from-autopilot-modal.see_more_features")
          }}
          <IIcon [name]="showMoreFeatures() ? 'chevronUp' : 'chevronDown'" />
        </button>
        <div class="type-caption text-muted-foreground flex items-center gap-2">
          <IIcon name="infoCircle" />
          {{ t("upgrade-from-autopilot-modal.back_to_autopilot_any_time") }}
        </div>
      </div>

      <div class="flex flex-col gap-3 rounded-xl border p-4">
        <div class="type-body text-muted-foreground flex items-center gap-2">
          <IIcon name="creditCard" />
          {{ t("upgrade-from-autopilot-modal.payment_summary") }}
        </div>
        <div class="type-body flex justify-between">
          <span class="text-foreground">{{ t("upgrade-from-autopilot-modal.pro_plan") }}</span>
          <span class="text-muted-foreground">{{ formattedPlanAmount() }}</span>
        </div>
        @if (autopilotCredit() > 0) {
          <div class="type-body flex justify-between">
            <span class="text-foreground flex items-center gap-2">
              {{ t("upgrade-from-autopilot-modal.autopilot_credits") }}
              <IIcon name="infoCircle" [iTooltip]="t('upgrade-from-autopilot-modal.autopilot_credits_tooltip')" />
            </span>
            <span class="text-muted-foreground">-{{ formattedAutopilotCredit() }}</span>
          </div>
        }
        <hr class="m-0 h-px w-full" />
        <div class="type-body flex items-center justify-between">
          <span class="text-foreground">{{ t("upgrade-from-autopilot-modal.due_today") }}</span>
          <span class="flex items-center gap-2">
            @if (proFreeTestEndDate()) {
              <IBadge color="gray" [label]="t('upgrade-from-autopilot-modal.free_test')" />
            }
            <span class="type-body-medium text-foreground">{{ formattedZeroAmount() }}</span>
          </span>
        </div>
        <hr class="m-0 h-px w-full" />
        <div class="type-caption text-muted-foreground">{{ t("upgrade-from-autopilot-modal.upcoming_payments") }}</div>
        <div class="type-body flex justify-between">
          <span class="text-foreground">{{ formattedFirstProChargeDate() }}</span>
          <span class="text-muted-foreground">
            {{
              t("upgrade-from-autopilot-modal.amount_plus_ad_spend", {
                amount: formattedFirstProChargeAmount(),
                percent: selectedPlan()?.adSpendFee?.amount ?? 0 | percent: "1.0-2" : locale(),
              })
            }}
          </span>
        </div>
        <div class="type-body flex justify-between">
          <span class="text-foreground">
            {{ t("upgrade-from-autopilot-modal.from_date", { date: formattedRecurringProChargeDate() }) }}
            <span class="type-caption text-muted-foreground">/ {{ t("upgrade-from-autopilot-modal.month") }}</span>
          </span>
          <span class="text-muted-foreground">
            {{
              t("upgrade-from-autopilot-modal.amount_plus_ad_spend", {
                amount: formattedPlanAmount(),
                percent: selectedPlan()?.adSpendFee?.amount ?? 0 | percent: "1.0-2" : locale(),
              })
            }}
          </span>
        </div>
      </div>
    </div>
  </ng-template>

  <ng-template #upgradeInformation>
    <div class="mx-1 mt-5 flex flex-col gap-2 rounded-xl border p-4">
      <div class="type-body text-muted-foreground flex items-center gap-2">
        <IIcon name="infoCircle" />
        {{ t("upgrade-from-autopilot-modal.upgrade_information") }}
      </div>
      <ul class="type-body text-foreground m-0 list-disc space-y-1 pl-5">
        @if (proFreeTestEndDate()) {
          <li>{{ t("upgrade-from-autopilot-modal.pro_free_test_starts", { days: PRO_FREE_TEST_DAYS }) }}</li>
        } @else {
          <li>{{ t("upgrade-from-autopilot-modal.pro_starts_now", { date: formattedFirstProChargeDate() }) }}</li>
        }
        @if (autopilotCredit() > 0) {
          <li>{{ t("upgrade-from-autopilot-modal.autopilot_credit", { amount: formattedAutopilotCredit() }) }}</li>
        }
        @if (autopilotFreeTestEndDate()) {
          <li>
            {{ t("upgrade-from-autopilot-modal.autopilot_free_test_ends", { date: autopilotFreeTestEndDate() }) }}
          </li>
        }
        @if (autopilotUpgradeCanceled()) {
          <li>{{ t("upgrade-from-autopilot-modal.autopilot_upgrade_canceled") }}</li>
        }
        <li>{{ t("upgrade-from-autopilot-modal.campaigns_stay_live") }}</li>
        <li>{{ t("upgrade-from-autopilot-modal.no_commitment") }}</li>
        @if (formattedAutopilotAmount()) {
          <li>
            {{ t("upgrade-from-autopilot-modal.back_to_autopilot_price", { amount: formattedAutopilotAmount() }) }}
          </li>
        }
      </ul>
    </div>
  </ng-template>

  <ng-template #proFeature let-label>
    <li class="type-body text-muted-foreground flex items-center gap-3">
      <span
        class="border-main-border bg-main-background text-main flex size-5 shrink-0 items-center justify-center rounded border"
      >
        <IIcon name="arrowUp" class="size-3" />
      </span>
      {{ label }}
    </li>
  </ng-template>
</ng-container>
